import React from "react";
import { LoadingButton, TextButton } from "kubra-ux-forge";
import Modal from "react-modal";
import Sheet from "react-modal-sheet";
import XButton from "assets/icons/x_button.svg";
import moment from "moment";
import { DONE, EMPTY, ERROR, IN_PROGRESS } from "constants/Status";
import { BANK_ACCCOUNT_MODAL, COMMON_NAMESPACE, PAYMENT_METHOD_MODAL_NAMESPACE, RECENT_PAYMENTS_NAMESPACE } from "constants/i18n-namespaces";
import { IPaymentRecord } from "types/Api/Payment/paymentRecord";
import { useTranslation } from "react-i18next";

const getLoadingButtonState = (
  cancelState: string
): "text" | "loading" | "success" => {
  switch (cancelState) {
    case EMPTY:
      return "text";
    case IN_PROGRESS:
      return "loading";
    case DONE:
      return "success";
    case ERROR:
      return "text";
    default:
      return "text";
  }
};

interface CancelPaymentModalProps {
  isOpen: boolean;
  isMobile: boolean;
  cancelState: string;
  cancelErrorMessage?: string;
  upcomingPayment: IPaymentRecord;
  onClose: () => void;
  onConfirmCancel: () => void;
  onReload: () => void;
}

export const CancelPaymentModal = ({
  isOpen,
  isMobile,
  cancelState,
  cancelErrorMessage,
  upcomingPayment,
  onClose,
  onConfirmCancel,
  onReload,
}: CancelPaymentModalProps) => {

  const { t } = useTranslation([
    RECENT_PAYMENTS_NAMESPACE,
    PAYMENT_METHOD_MODAL_NAMESPACE,
    BANK_ACCCOUNT_MODAL,
  ]);
  
  const modalContent = (
    <div className="cancel-payment-content-container">
      <div className="cancel-payment-content">
        {cancelState === DONE
          ? t("cancel-payment-modal-content-success")
          : t("cancel-payment-modal-content", {
              date: moment.utc(upcomingPayment.paymentDate).format("MM/DD/YY"),
            })}
      </div>
      <div className="comfirm-cancel-modal-buttons-row">
        {cancelState === DONE ? (
          <button 
            className="done-button" 
            data-cy="button-close"
            onClick={onReload}>
              {t("close")}
          </button>
        ) : (
          <>
            <TextButton onClick={onClose}>{t("no-cancel")}</TextButton>
            <div>
              <LoadingButton
                loadingState={getLoadingButtonState(cancelState)}
                errorMessage={cancelErrorMessage}
                className="done-button"
                onClick={onConfirmCancel}
              >
                {t("yes-cancel")}
              </LoadingButton>
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet
        isOpen={isOpen}
        onClose={onClose}
        detent="content-height"
        disableDrag={true}
      >
        <Sheet.Container>
          <Sheet.Header>
            <div className="react-modal-sheet-header">
              <div className="help-modal-header-text">
                {cancelState === DONE
                  ? t("cancel-payment-modal-title-success")
                  : t("cancel-payment-modal-title")}
              </div>
              <button
                className="modal-close"
                onClick={onClose}
                aria-label={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
                data-cy="button-close"
              >
                <img
                  src={XButton}
                  alt={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
                  className="modal-close-x"
                />
              </button>
            </div>
          </Sheet.Header>
          <Sheet.Content>{modalContent}</Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop />
      </Sheet>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      ariaHideApp={false}
      className="modal-frame-help"
      shouldCloseOnOverlayClick={false}
      onRequestClose={onClose}
    >
      <div className="modal-header">
        <div className="help-modal-header-text">
          {cancelState === DONE
            ? t("cancel-payment-modal-title-success")
            : t("cancel-payment-modal-title")}
        </div>
        <button
          className="modal-close"
          onClick={onClose}
          data-cy="button-close"
          aria-label={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
        >
          <img
            src={XButton}
            alt={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
            className="modal-close-x"
          />
        </button>
      </div>
      {modalContent}
    </Modal>
  );
};
